/**
 * PainPointsSection Component
 * Displays industry-specific pain points gathered from Fireflies transcripts,
 * email conversations, and manual entry. Enterprise-tier focused.
 */

import React, { useState } from 'react';

const PAIN_POINT_ICONS = {
  connectivity: '\u{1F4F6}',
  security: '\u{1F512}',
  cost: '\u{1F4B0}',
  downtime: '\u26A0\uFE0F',
  scalability: '\u{1F4C8}',
  compliance: '\u{1F4CB}',
  legacy: '\u{1F527}',
  communication: '\u{1F4DE}',
  default: '\u{1F534}',
};

const styles = {
  section: {
    padding: '60px 20px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '48px',
  },
  badge: {
    display: 'inline-block',
    padding: '6px 16px',
    backgroundColor: 'rgba(255, 107, 107, 0.15)',
    color: '#FF6B6B',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    marginBottom: '16px',
  },
  title: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '12px',
  },
  subtitle: {
    fontSize: '16px',
    color: '#8B92A5',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.6',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
    gap: '20px',
  },
  card: {
    backgroundColor: '#1A2332',
    borderRadius: '12px',
    padding: '28px',
    border: '1px solid #2A3444',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
  },
  cardHover: {
    borderColor: '#FF6B6B',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(255, 107, 107, 0.1)',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    marginBottom: '14px',
  },
  cardIcon: {
    fontSize: '28px',
    lineHeight: '1',
    flexShrink: 0,
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#FFFFFF',
    lineHeight: '1.3',
  },
  cardDescription: {
    fontSize: '14px',
    color: '#8B92A5',
    lineHeight: '1.6',
    marginBottom: '16px',
  },
  cardSolution: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 16px',
    backgroundColor: 'rgba(0, 212, 170, 0.08)',
    borderRadius: '8px',
    border: '1px solid rgba(0, 212, 170, 0.2)',
  },
  solutionIcon: {
    color: '#00D4AA',
    fontSize: '16px',
    flexShrink: 0,
  },
  solutionText: {
    fontSize: '13px',
    color: '#00D4AA',
    fontWeight: '500',
    lineHeight: '1.4',
  },
  sourceTag: {
    display: 'inline-block',
    padding: '3px 8px',
    backgroundColor: 'rgba(0, 87, 255, 0.15)',
    color: '#5B9DFF',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '500',
    marginTop: '12px',
  },
};

// Default pain points if none provided by backend
const DEFAULT_PAIN_POINTS = [
  {
    id: 1,
    category: 'connectivity',
    title: 'Unreliable Internet & Connectivity',
    description: 'Current provider delivers inconsistent speeds, frequent dropouts, and poor SLA response times affecting daily operations.',
    solution: 'Talk Australia provides enterprise-grade connectivity with 99.95% uptime SLA and priority support.',
    source: 'discovery-call',
  },
  {
    id: 2,
    category: 'security',
    title: 'Cybersecurity Gaps',
    description: 'No unified security strategy, outdated firewalls, and staff without awareness training leave the business vulnerable.',
    solution: 'Our managed security stack includes next-gen firewall, endpoint protection, and staff awareness training.',
    source: 'fireflies',
  },
  {
    id: 3,
    category: 'cost',
    title: 'Fragmented Vendor Costs',
    description: 'Multiple vendors for phone, internet, mobiles, and IT support with no single point of accountability.',
    solution: 'One bill, one provider, one support number. We consolidate and typically save 20-30%.',
    source: 'email',
  },
  {
    id: 4,
    category: 'communication',
    title: 'Outdated Phone System',
    description: 'Legacy PBX with limited features, no mobile integration, and expensive call costs.',
    solution: 'Modern UCaaS platform with Teams integration, mobile apps, and unlimited national calls included.',
    source: 'discovery-call',
  },
];

function getSourceLabel(source) {
  switch (source) {
    case 'fireflies':
      return 'From Meeting Transcript';
    case 'email':
      return 'From Email Conversation';
    case 'discovery-call':
      return 'From Discovery Call';
    case 'manual':
      return 'Identified by Account Manager';
    default:
      return 'Identified';
  }
}

function PainPointsSection({ prospect }) {
  const [hoveredCard, setHoveredCard] = useState(null);

  const painPoints = prospect?.painPoints?.length > 0
    ? prospect.painPoints
    : DEFAULT_PAIN_POINTS;

  return (
    <div style={styles.section}>
      <div style={styles.header}>
        <div style={styles.badge}>What We Heard</div>
        <h2 style={styles.title}>
          Your Challenges, {prospect?.contactName?.split(' ')[0] || 'Understood'}
        </h2>
        <p style={styles.subtitle}>
          Based on our conversations, here are the key challenges we've identified
          {prospect?.companyName ? ` for ${prospect.companyName}` : ''} — and how we solve each one.
        </p>
      </div>

      <div style={styles.grid}>
        {painPoints.map((point, index) => {
          const isHovered = hoveredCard === index;
          const icon = PAIN_POINT_ICONS[point.category] || PAIN_POINT_ICONS.default;

          return (
            <div
              key={point.id || index}
              style={{
                ...styles.card,
                ...(isHovered ? styles.cardHover : {}),
              }}
              onMouseEnter={() => setHoveredCard(index)}
              onMouseLeave={() => setHoveredCard(null)}
            >
              <div style={styles.cardHeader}>
                <span style={styles.cardIcon}>{icon}</span>
                <span style={styles.cardTitle}>{point.title}</span>
              </div>
              <p style={styles.cardDescription}>{point.description}</p>
              {point.solution && (
                <div style={styles.cardSolution}>
                  <span style={styles.solutionIcon}>\u2713</span>
                  <span style={styles.solutionText}>{point.solution}</span>
                </div>
              )}
              {point.source && (
                <span style={styles.sourceTag}>{getSourceLabel(point.source)}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default PainPointsSection;
